/**
 * The warning chip on a station row (station badge, weather-warnings Phase B
 * MR6), from one point's `GET /api/warnings/summary/` item.
 *
 * The chip is the lead hazard's glyph plus its severity WORD on the tone's
 * chip fill (the word carries the level, never the colour alone, WCAG 1.4.1).
 * Nothing in force yet (`active: false`) draws it outlined. The count is not
 * drawn; the spoken name carries it ("and 2 more").
 *
 * The lead (`warning_type`, `severity`) is the backend's, which mirrors this
 * package's `compareHazardGroups` (`warningsSummaryItem`): the badge never
 * re-derives it, so it always names the hazard the dashboard card's band
 * names.
 *
 * Imports the leaf modules, never the utils barrel (`importCycles.test.ts`).
 */
import {messages} from '@smartmixin/i18n';

import type {WarningType, WarningsSummaryItem} from '../../api/types';
import {normaliseType} from './model';
import {
  HAZARD_MESSAGES,
  SEVERITY_MESSAGES,
  WarningTone,
  WarningsIntl,
  severityTone,
} from './view';

export type StationBadgeViewModel = {
  /** Each app's theme maps it to the chip / onChip colours. */
  tone: WarningTone;
  /** The lead hazard; each app maps it to its glyph (`WARNING_TYPE_ICONS`). */
  hazard: WarningType;
  /** The severity word drawn on the chip, e.g. "Severe". */
  word: string;
  /** No warning has started yet: drawn as an outline, not filled. */
  outlined: boolean;
  /**
   * The chip read aloud: "Official warning, Severe: Rain, and 2 more", or
   * "Upcoming official warning, …" when nothing has started. No time: the
   * summary does not carry one.
   */
  accessibleName: string;
};

/**
 * Null when the station shows no chip: no item (no warning, not loaded, or
 * the request failed), or a level the point endpoint never serves (minor, and
 * unknown, which has no word and would be a colour-only chip).
 */
export const stationBadgeViewModel = (
  item: WarningsSummaryItem | null | undefined,
  intl: WarningsIntl,
): StationBadgeViewModel | null => {
  // Minor is dropped by the summary as by the point endpoint; unknown (or a
  // level this build does not know) has no word.
  const severity =
    item && item.severity !== 'minor'
      ? (SEVERITY_MESSAGES[item.severity] ?? null)
      : null;
  if (!item || !severity) {
    return null;
  }
  const hazard = normaliseType(item.warning_type);
  const word = intl.formatMessage(severity);
  const count = Number.isFinite(item.count) ? Math.floor(item.count) : 1;
  const outlined = item.active === false;
  return {
    tone: severityTone(item.severity),
    hazard,
    word,
    outlined,
    accessibleName: intl.formatMessage(
      outlined
        ? messages.weatherWarnings.badgeLabelUpcoming
        : messages.weatherWarnings.badgeLabel,
      {
        others: Math.max(0, count - 1),
        severity: word,
        hazard: intl.formatMessage(HAZARD_MESSAGES[hazard]),
      },
    ),
  };
};
